import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { Logo } from '../ui/Logo';
import { useSiteTheme } from '../lib/palette';

/** Matches AMBASSADOR_TERMS_VERSION on the server, which stores it with each application. */
const UPDATED = 'October 28, 2026'; // tiers by paid sign-ups and followers (#902); before that, no-charge access and inactivity (#895)

/**
 * The Brand Ambassador terms (#715), at /ambassador-terms for anyone. Each ambassador's own rates live on their private
 * page and in their welcome email, so the terms point there rather than naming numbers.
 */
export interface Tier {
  name: string;
  commissionPct: number;
  commissionMonths: number;
  discountPct: number;
  discountMonths: number;
  minSignups: number;
  minFollowers: number;
  isDefault: boolean;
}
export const reachText = (t: Tier) =>
  !t.minSignups && !t.minFollowers
    ? 'Where everyone starts'
    : [t.minSignups ? `${t.minSignups.toLocaleString()} paid sign-ups` : '', t.minFollowers ? `more than ${t.minFollowers.toLocaleString()} followers` : '']
        .filter(Boolean)
        .join(' or ');

export function AmbassadorTerms() {
  const [contact, setContact] = useState('');
  // The inactivity and access numbers from Admin → Partners (#895), so the terms say what the program does.
  const [n, setN] = useState({ inactiveMonths: 12, inactivityNoticeDays: 30, accessAfterEndDays: 30 });
  // The tier ladder (#902), lowest first, from Admin → Partners → Tiers.
  const [tiers, setTiers] = useState<Tier[]>([]);
  useSiteTheme();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Brand Ambassador terms · ClassGuardian';
    api
      .get<{ contact: string; inactiveMonths?: number; inactivityNoticeDays?: number; accessAfterEndDays?: number; tiers?: Tier[] }>('/api/ambassadors')
      .then((p) => {
        setContact(p.contact);
        setTiers(p.tiers ?? []);
        if (p.inactiveMonths) setN({ inactiveMonths: p.inactiveMonths, inactivityNoticeDays: p.inactivityNoticeDays ?? 30, accessAfterEndDays: p.accessAfterEndDays ?? 30 });
      })
      .catch(() => undefined);
  }, []);
  return (
    <div className="legal-page">
      <header className="legal-head">
        <Link to="/" aria-label="ClassGuardian home">
          <Logo />
        </Link>
      </header>
      <main className="legal-body">
        <h1>Brand Ambassador terms</h1>
        <p className="muted">Last updated {UPDATED}</p>

        <p>
          These terms are an agreement between you and Vieira Ventures LLC (“we”, “us”), which runs ClassGuardian, for taking part in the ClassGuardian Brand
          Ambassador program. They sit alongside our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>. By applying, you
          agree to them.
        </p>

        <h2>1. Joining</h2>
        <p>
          Apply on the <Link to="/ambassadors">Brand Ambassadors page</Link>. We read every application and decide who joins. You must be 18 or older and able
          to receive payments. One ambassador spot per person or organization.
        </p>

        <h2>2. Your code and link</h2>
        <p>
          Once you’re in, you get your own code, a sign-up link and a Brand Ambassador page in your ClassGuardian account, which shows your sign-ups and
          earnings. Your account is protected by your password and two-factor sign-in; keep them to yourself. A family counts as yours only if it’s
          new to ClassGuardian and uses your code or link when it signs up. We can’t move a family to you after it has signed up.
        </p>

        <h2>3. What families get</h2>
        <p>Families who use your code get the discount shown in your welcome email and on your Brand Ambassador page, applied automatically at checkout for the months stated.</p>

        <h2>4. Your own ClassGuardian</h2>
        <p>
          While you’re an active ambassador, your own family can use ClassGuardian on the plan shown on your Brand Ambassador page at no charge. This is part of
          the program, not a free plan for life: it ends when your part in the program ends, for any reason. After that your family keeps full access for{' '}
          {n.accessAfterEndDays} days, then you choose a plan to keep going. Your records stay yours either way.
        </p>

        <h2>5. What you earn</h2>
        <p>
          You earn the percentage shown on your Brand Ambassador page of what each of your families actually pays (after their discount, not counting tax) for the months
          stated, starting with their first payment. Your own family, and families in your household, don’t count. Free trials and free plans earn nothing until
          the family pays.
        </p>

        <h2>6. Tiers</h2>
        <p>
          What you earn and the discount your families get depend on your tier. Everyone starts on the first tier below, and you move up automatically when you
          reach the next tier’s paid sign-ups or followers, whichever comes first. A sign-up counts only once that family makes its first payment; families on
          a free trial that never pay don’t count. Your followers are the number you give us for your largest platform; we may check it, and a new number
          counts once we’ve approved it. Moving up changes your rates for payments from then on, including from families you already brought in, and the
          discount for families who sign up after it; what you’ve already earned doesn’t change. You don’t move down automatically, but we may change your tier
          if we find a sign-up or follower count wasn’t genuine.
        </p>
        {tiers.length > 0 && (
          <div className="legal-table">
            <table>
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>You earn</th>
                  <th>Your families get</th>
                  <th>Reached at</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t) => (
                  <tr key={t.name}>
                    <td>{t.name}</td>
                    <td>
                      {t.commissionPct}% for {t.commissionMonths} months
                    </td>
                    <td>
                      {t.discountPct}% off for {t.discountMonths} months
                    </td>
                    <td>{reachText(t)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p>We may change the tiers, their rates and what reaches them with 30 days’ notice by email, as section 14 describes.</p>

        <h2>7. When and how you’re paid</h2>
        <p>
          We pay you through Stripe, our payment provider. To be paid, set up payouts from your Brand Ambassador page: Stripe collects your identity, bank
          account and tax information on its own secure pages, under the{' '}
          <a href="https://stripe.com/legal/connect-account" target="_blank" rel="noreferrer">
            Stripe Connected Account Agreement
          </a>
          , and we never see your tax ID or bank details. Each earning becomes payable after the waiting period shown on your Brand Ambassador page, so a refund or
          chargeback can come back first; if a payment is refunded or charged back, its earning is removed. Once what you’re owed reaches the minimum on your
          page, we send it to your Stripe account, which pays it to your bank. We may instead pay you another way we agree on, such as credit toward your own
          ClassGuardian subscription. You’re responsible for your own taxes; you’ll get a tax form (such as a 1099) where the law requires.
        </p>

        <h2>8. Sharing honestly</h2>
        <p>
          Whenever you share your code or link, say clearly that you’re a ClassGuardian Brand Ambassador and earn from sign-ups, as the FTC and the platforms you
          use require. Only say true things about ClassGuardian, and don’t promise results, such as grades or meeting your state’s homeschool requirements, beyond
          what ClassGuardian itself says. Follow the rules of each platform you post on.
        </p>

        <h2>9. What’s not allowed</h2>
        <ul>
          <li>Spam, or messages to people who didn’t ask to hear from you.</li>
          <li>Paid search ads on “ClassGuardian” or names like it, or ads that look like they’re from us.</li>
          <li>Posting your code on coupon or deal sites.</li>
          <li>Pretending to be ClassGuardian, or making accounts, pages or handles in our name.</li>
          <li>Paying or rewarding people to sign up, or signing up families yourself.</li>
          <li>Asking children to sign up; ClassGuardian is for parents.</li>
        </ul>
        <p>Earnings that come from breaking these terms aren’t owed.</p>

        <h2>10. Our name and logo</h2>
        <p>
          While you’re an ambassador, you may use the ClassGuardian name and the logo we provide, only to share ClassGuardian, and without changing them. That
          permission ends when you leave the program.
        </p>

        <h2>11. You’re independent</h2>
        <p>You take part as an independent creator, not as our employee, agent or partner, and you can’t make promises or agreements for us.</p>

        <h2>12. Families’ privacy</h2>
        <p>
          Your Brand Ambassador page shows counts only, never families’ names or details. We keep your name, email, the links you gave us and your earnings to run the
          program, as our <Link to="/privacy">Privacy Policy</Link> describes.
        </p>

        <h2>13. Inactivity</h2>
        <p>
          If no new family signs up with your code for {n.inactiveMonths} months, we’ll email you. If there’s still no new sign-up {n.inactivityNoticeDays} days
          after that email, your part in the program ends and your code stops working for new families. If a new family signs up in those{' '}
          {n.inactivityNoticeDays} days, nothing changes. Families who already used your code keep their discount, and you keep earning on them for the months
          stated. Your own family’s no-charge access then ends as section 4 describes. You can apply again at any time.
        </p>
        <p>
          Earnings you haven’t collected, because payouts aren’t set up or a payment to you failed, stay yours. We’ll email you reminders. If they still aren’t
          claimed, we handle them as the unclaimed-property laws require, which can mean sending them to your state to hold for you.
        </p>

        <h2>14. Changes and leaving</h2>
        <p>
          You or we can end your part in the program at any time, by email. We can pause your code if we think these terms were broken. When you leave, we pay
          what you’ve earned once its waiting period is over, even if it’s below the minimum, unless it came from breaking these terms, and your family’s
          no-charge access ends as section 4 describes. We may change the program or these terms with 30 days’ notice by email; changes never reduce what
          you’ve already earned.
        </p>

        <h2>15. Limits and the law that applies</h2>
        <p>
          The disclaimers and limits in our <Link to="/terms">Terms of Service</Link> apply to the program too. These terms are governed by the laws of the State
          of South Carolina and the United States, without regard to conflict-of-law rules.
        </p>

        <h2>16. Contact</h2>
        <p>{contact ? <>Questions about the program: <a href={`mailto:${contact}`}>{contact}</a>.</> : 'Questions about the program: use the contact details on our home page.'}</p>
      </main>
      <footer className="landing-footer muted small">
        <Logo /> <span>Homeschool, organized. School time, focused.</span>
        <span className="landing-footer-links">
          <Link to="/ambassadors" className="landing-footer-link">
            Brand Ambassadors
          </Link>
          <Link to="/terms" className="landing-footer-link">
            Terms
          </Link>
          <Link to="/privacy" className="landing-footer-link">
            Privacy Policy
          </Link>
        </span>
      </footer>
    </div>
  );
}
